import { Fade, Stack, TextField, IconButton, InputAdornment } from '@mui/material';
import { debounce } from 'lodash';
import { t } from 'i18next';
import React, { useRef, useState, useCallback, useEffect } from 'react';
import Iconify from '../../media/iconify';

interface SearchBarProps {
  filters?: any;
  handleFilterSearch?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleFilters?: (key: string, value: any) => void;
}

export default function SearchBar({ filters, handleFilterSearch, handleFilters }: SearchBarProps) {
  const searchTextRef = useRef<HTMLInputElement>();
  const [focused, setFocused] = useState(false);
  const [hasValue, setHasValue] = useState(!!filters?.search?.length);

  // Sync input value whenever filters.search changes (including when cleared externally)
  useEffect(() => {
    if (searchTextRef.current) {
      searchTextRef.current.value = filters?.search || '';
      setHasValue(!!filters?.search?.length);
    }
  }, [filters?.search]);

  const handleClear = useCallback(() => {
    if (searchTextRef?.current) {
      searchTextRef.current.value = '';
      searchTextRef.current.focus();
    }
    handleFilters?.('search', '');
    setHasValue(false);
  }, [handleFilters]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setHasValue(!!value.length);
      if (handleFilterSearch) {
        handleFilterSearch(event);
      }
    },
    [handleFilterSearch]
  );

  const debouncedHandleChange = React.useMemo(() => debounce(handleChange, 300), [handleChange]);

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={{ xs: 1, sm: 2 }}
      flexGrow={1}
      sx={{
        width: 1,
        minWidth: 0,
      }}
    >
      <TextField
        fullWidth
        inputRef={searchTextRef}
        defaultValue={filters?.search || ''}
        onChange={debouncedHandleChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={t('Search organizations, contacts, or any data...')}
        size="small"
        sx={{
          '& .MuiInputBase-input': {
            fontSize: '0.875rem',
            py: 1.25,
            pl: 1.5,
            '&::placeholder': {
              color: 'text.disabled',
              opacity: 0.8,
            },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Iconify
                icon="solar:magnifer-linear"
                sx={{
                  color: focused ? 'text.secondary' : 'text.disabled',
                  width: 20,
                  height: 20,
                  pr: 0.5,
                  transition: 'color 0.2s ease-in-out',
                }}
              />
            </InputAdornment>
          ),
          endAdornment:
            hasValue || !!filters?.search?.length ? (
              <Fade in={hasValue || !!filters?.search?.length}>
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClear}
                    sx={{
                      borderRadius: 1,
                      p: 0.5,
                      color: 'text.secondary',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        color: 'text.primary',
                      },
                    }}
                  >
                    <Iconify
                      icon="material-symbols:close-rounded"
                      sx={{
                        width: 16,
                        height: 16,
                      }}
                    />
                  </IconButton>
                </InputAdornment>
              </Fade>
            ) : null,
        }}
      />
    </Stack>
  );
}
